import React, { useCallback, useEffect, useState } from 'react';
import {
  IconButton,
  MenuItem,
  Button,
  Menu,
  Badge,
} from '@mui/material';
import FlakyOutlinedIcon from '@mui/icons-material/FlakyOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/system';
import KeycloakService from '../../services/KeycloakService';
import NotificationCard from './NotificationCard';
import {
  clearAllNotification,
  clearNotification,
  getAllNotifications,
} from '../../services/NotificationService';
import { DialogBoxComponent } from '../DialogBox/DialogBoxCard';

/** Matches the scrollable menu (~450px); enough for the visible UI without unbounded DOM. */
export const NOTIFICATION_PAGE_SIZE = 50;

const POLL_INTERVAL_MS = 50000;

const sameNotificationIds = (prev, next) => {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i += 1) {
    if (prev[i].id !== next[i].id) return false;
  }
  return true;
};

/**
 * Owns notification fetch/poll/menu state so Toolbar route/mode re-renders
 * do not rebuild NotificationCard trees (React.memo + no location props).
 */
function ToolbarNotifications() {
  const navigate = useNavigate();
  const [anchorElBell, setAnchorElBell] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState();
  const menuOpen = Boolean(anchorElBell);

  const applyNotificationPayload = useCallback((page) => {
    const next = page?.content ?? [];
    const total =
      typeof page?.totalElements === 'number' ? page.totalElements : next.length;

    setUnreadCount((prev) => (prev === total ? prev : total));
    setNotifications((prev) =>
      sameNotificationIds(prev, next) ? prev : next,
    );
  }, []);

  const fetchNotifications = useCallback(async () => {
    const notificationList = await getAllNotifications(
      KeycloakService.getUserDetail(),
      { pageNumber: 0, pageSize: NOTIFICATION_PAGE_SIZE },
    );
    if (notificationList.isError === false) {
      applyNotificationPayload(notificationList.data);
    }
  }, [applyNotificationPayload]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(() => {
      fetchNotifications();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNotifications, refreshToken]);

  const requestRefresh = () => {
    setRefreshToken((token) => token + 1);
  };

  const openNotifications = (event) => {
    setAnchorElBell(event.currentTarget);
    requestRefresh();
  };

  const closeNotifications = () => {
    setAnchorElBell(null);
  };

  const handleNotificationClick = async (message, id, link) => {
    if (link == '' || message.includes('cancelled')) {
      setDialogContent(message);
      setOpenDialog(true);
    } else {
      navigate(link);
    }
    closeNotifications();
    const clear = await clearNotification(id);
    if (clear.isError === false) {
      requestRefresh();
    }
  };

  const removeNotification = async (event, id) => {
    event.stopPropagation();
    const clear = await clearNotification(id);
    if (clear.isError === false) {
      requestRefresh();
    }
  };

  const removeAllNotifications = async () => {
    const clearAll = await clearAllNotification(
      KeycloakService.getUserDetail(),
    );
    if (clearAll.isError === false) {
      requestRefresh();
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const hasUnread = unreadCount > 0;

  return (
    <>
      <DialogBoxComponent
        open={openDialog}
        handleClose={handleCloseDialog}
        confirmText={'Ok'}
        content={dialogContent}
        onConfirm={handleCloseDialog}
        onCancel={handleCloseDialog}
        icon={<FlakyOutlinedIcon color="primary" />}
      />
      <IconButton onClick={openNotifications}>
        {!hasUnread ? (
          <NotificationsNoneIcon />
        ) : (
          <Badge badgeContent={unreadCount} color="primary">
            <NotificationsIcon color="action" />
          </Badge>
        )}
      </IconButton>
      <Menu
        anchorEl={anchorElBell}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={menuOpen}
        onClose={closeNotifications}
        PaperProps={{
          style: {
            maxHeight: 450,
            width: '40ch',
          },
        }}
      >
        {menuOpen && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '0 16px',
              }}
            >
              <Button
                sx={{ padding: '0' }}
                onClick={removeAllNotifications}
                hidden={!hasUnread}
              >
                Clear All
              </Button>
            </Box>
            {hasUnread && notifications.length > 0 ? (
              notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() =>
                    handleNotificationClick(
                      notification.notificationDTO.message,
                      notification.id,
                      notification.notificationDTO.link,
                    )
                  }
                >
                  <NotificationCard
                    notification={notification}
                    message={notification.notificationDTO.message}
                    notificationExists
                    removeNotification={removeNotification}
                  />
                </MenuItem>
              ))
            ) : (
              <MenuItem onClick={closeNotifications}>
                <NotificationCard notificationExists={false} />
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </>
  );
}

export default React.memo(ToolbarNotifications);
